import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Row } from "reactstrap";
import CheckboxField from "src/components/Form/CheckboxFieldV2/CheckboxFieldV2";
import RadioButton from "src/components/Form/RadioButtonV2/RadioButton";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import TypeaheadField from "src/components/Form/TypeaheadFieldV2/TypeaheadFieldV2";
import useAutosave from "src/hooks/useAutosave";
import { useLoading } from "src/hooks/useLoading";
import { IRootState } from "src/store/reducers";
import { appBuilderShouldSave } from "src/store/reducers/applicationBuilder";
import { fetchLtcDetails, saveLtcDetails } from "src/store/reducers/applicationLtc";
import * as S from "../../../../constants/StringConstants";
import { TabProps } from "../../pages";

export const AppLTCDetail = (props: TabProps) => {
  const { id: applicationId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { setIsLoading } = useLoading();
  const [formRef, setFormRef] = useState(null);

  const { userID, ltcDetails } = useSelector((state: IRootState) => ({
    userID: state.auth.user.userID,
    ltcDetails: state.applicationLtc.ltcDetails,
  }));

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchLtcDetails(applicationId));
    setIsLoading(false);
  }, [dispatch, applicationId]);

  const performSave = async () => {
    dispatch(appBuilderShouldSave(false));

    if (!formRef) return;
    const state = formRef.getState();

    const payload = {
      ...state.values,
      ltcid: ltcDetails?.ltcid || 0,
      applicationID: applicationId,
      createdBy: userID,
    };

    try {
      await dispatch(saveLtcDetails(applicationId, payload));
      await dispatch(fetchLtcDetails(applicationId));
    } catch (error) {}
  };

  const AutoSave = useAutosave(props, performSave);

  return (
    <Form initialValues={ltcDetails} onSubmit={performSave}>
      {({ form, dirty }) => {
        if (!formRef) {
          setFormRef(form);
        }

        useEffect(() => {
          props.onDirty(dirty);
        }, [dirty]);

        return (
          <form onSubmit={form.submit}>
            <Row>
              <Col sm="3">
                <Row>
                  <Col className="app-form-header">
                    <h4 className="form-title">{S.APP_LTC_DETAILS_TITLE}</h4>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <TextField name="dailyMonthlyBenefitAmount" label={S.APP_LTC_DAILY_BENEFIT_AMOUNT} />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TypeaheadField
                      name="benefitPeriodTypeID"
                      label={S.APP_LTC_BENEFIT_PERIOD}
                      options={ltcDetails?.selectBenefitPeriodTypeList}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TypeaheadField
                      name="eliminationPeriodTypeID"
                      label={S.APP_LTC_ELIMINATION_PERIOD}
                      options={ltcDetails?.selectEliminationPeriodTypeList}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TypeaheadField
                      name="payoutTypeID"
                      label={S.APP_LTC_PREMIUM_MODE}
                      options={ltcDetails?.selectPayoutTypeList}
                    />
                  </Col>
                </Row>
              </Col>

              <Col sm="6">
                <Row>
                  <Col className="app-form-header">
                    <h4 className="form-title">{S.APP_LTC_RIDERS_TITLE}</h4>
                  </Col>
                </Row>

                <Row className="app-checkbox-container">
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="comprehensive" label={S.APP_LTC_RIDER_COMPREHENSIVE} />
                        <CheckboxField name="facilityCareServiceOnly" label={S.APP_LTC_RIDER_FACILITY_CARE} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="compoundInflation" label={S.APP_LTC_RIDER_COMPOUND_INFLATION} />
                        {ltcDetails?.selectCompoundInflationTypeList?.map(option => (
                          <RadioButton
                            key={option.value}
                            name="compoundInflationTypeID"
                            label={option.key}
                            value={option.value}
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="benefitPeriodExtension" label={S.APP_LTC_RIDER_BENEFIT_PERIOD_EXTENSION} />
                        {ltcDetails?.selectBenefitPeriodExtensionTypeList?.map(option => (
                          <RadioButton
                            key={option.value}
                            name="benefitPeriodExtensionTypeID"
                            label={option.key}
                            value={option.value}
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField
                          name="shortenedBenefitPeriodNonForfeiture"
                          label={S.APP_LTC_RIDER_SHORTENED_BENEFIT_PERIOD_NON_FORFEITURE}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="returnOfPremium" label={S.APP_LTC_RIDER_RETURN_OF_PREMIUM} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="sharedBenefitAmount" label={S.APP_LTC_RIDER_SHARED_BENEFIT_AMOUNT} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="waiverOfPremium" label={S.APP_LTC_RIDER_WAIVER_OF_PREMIUM} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="firstDayHCCSBenefit" label={S.APP_LTC_RIDER_FIRST_DAY_HCCS_BENEFIT} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="app-form-checkbox-radio-button-container">
                        <CheckboxField name="otherRider" label={S.APP_LTC_RIDER_OTHER} />
                        <TextField name="otherRiderDetails" bare />
                      </div>
                    </Col>
                  </Row>
                </Row>
              </Col>
            </Row>
            <AutoSave />
          </form>
        );
      }}
    </Form>
  );
};
